import {memo, useEffect, useRef} from 'react'
import {PullRequestDiff, type PullRequestDiffProps} from '../PullRequestDiff'
import {LazyDiffEntryLoadingSkeleton} from './LazyDiffEntryLoadingSkeleton'
import {EagerDiffEntryLoadingSkeleton} from './EagerDiffEntryLoadingSkeleton'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestDiffEntryErrorFallback} from '../PullRequestDiffEntryErrorFallback'
import {DiffFileHeader} from '@github-ui/diff-file-header'
import styles from '@github-ui/diff-lines/Diff.module.css'
import {clsx} from 'clsx'
import {noop} from '@github-ui/noop'
import {HiddenDiffEntryLoadingSkeleton} from './HiddenDiffEntryLoadingSkeleton'
import {isSelectingDiffLineOrRange, parseDiffHash} from '@github-ui/diffs/document-hash-helpers'
import {ssrSafeLocation, ssrSafeWindow} from '@github-ui/ssr-utils'
import {useDiffEntry, useDiffSummary, useIsSelectedFile, useIsSingleFileMode} from '../../stores/selectors'
import type {ScrollToAndFocusFunction} from '../../hooks/use-scroll-to-and-focus'
import type {ProgressiveDiffEntry} from '../../types/progressive-diff-types'

export type ProgressivePullRequestDiffEntryProps = {
  progressiveDiffEntry: ProgressiveDiffEntry
  onScrollToAndFocusEntry: ScrollToAndFocusFunction
  // this is a workaround to validate memoization of the component in the test environment
  testRenderFn?: () => void
} & Pick<
  PullRequestDiffProps,
  | 'diffManuallyExpanded'
  | 'headBranchName'
  | 'headRepoName'
  | 'headRepoOwner'
  | 'pullRequestGlobalRelayId'
  | 'repository'
>

export const ProgressivePullRequestDiffEntry = memo(
  ProgressivePullRequestDiffEntryUnmemoized,
  (prevProps, nextProps) => {
    return (
      prevProps.progressiveDiffEntry.pathDigest === nextProps.progressiveDiffEntry.pathDigest &&
      prevProps.progressiveDiffEntry.renderMode === nextProps.progressiveDiffEntry.renderMode
    )
    // TODO: add props for other cases where we want the diff entry to re-render
  },
)

function ProgressivePullRequestDiffEntryUnmemoized({
  diffManuallyExpanded,
  headBranchName,
  headRepoName,
  headRepoOwner,
  onScrollToAndFocusEntry,
  progressiveDiffEntry,
  pullRequestGlobalRelayId,
  repository,
  testRenderFn,
}: ProgressivePullRequestDiffEntryProps) {
  const isSelectedFile = useIsSelectedFile(progressiveDiffEntry.pathDigest)
  const diffEntry = useDiffEntry(progressiveDiffEntry.path)
  const diffSummary = useDiffSummary(progressiveDiffEntry.path)
  const isInSingleFileMode = useIsSingleFileMode()

  if (testRenderFn) testRenderFn()
  /*
   * Scroll to and focus the selected diff entry or line(s) on first visible render.
   * To ensure this effect only happens on first visible render, we use a ref to track if we have run it before.
   */
  const skipScrollToAndFocusRef = useRef(false)

  useEffect(() => {
    if (skipScrollToAndFocusRef.current) return

    const existingHash = ssrSafeLocation.hash ?? ''
    let windowHash = parseDiffHash(existingHash) ?? ''
    windowHash = windowHash.replace('diff-', '').replace('#', '')
    if (windowHash !== progressiveDiffEntry.pathDigest) {
      // if the window hash on this very first run is empty, there is not a file we will eventually be scrolling to
      // when the isSelectedFile value catches up
      skipScrollToAndFocusRef.current = true
    }

    // Don't set skipScrollToAndFocusRef to true if the file is selected but the data for file is still being fetched.
    if (isSelectedFile && ['LAZY_AUTO_LOAD', 'EAGER_AUTO_LOAD'].includes(progressiveDiffEntry.renderMode)) {
      return
    }

    if (isSelectedFile && progressiveDiffEntry.renderMode !== 'HIDE') {
      // If entry is selected, don't scroll and focus until we've rendered something
      const diffAnchorInURL = ssrSafeWindow?.location.hash.substring(1) ?? ''
      const isSelectingLineOrRange = isSelectingDiffLineOrRange(diffAnchorInURL)

      // If we're selecting an entry, not a line or range, scroll and focus right away, even if we're rendering a placeholder
      if (!isSelectingLineOrRange || progressiveDiffEntry.renderMode === 'RENDER') {
        const focusedElement = ssrSafeWindow?.document.activeElement
        // If the filters have previously been interacted with and the focus is within the filters, assume that the
        // diff was added to the DOM by removing a filter in which case we don't want to move focus from the filter.
        if (focusedElement?.closest('#diff-file-tree-filter')) {
          return
        }

        onScrollToAndFocusEntry(progressiveDiffEntry.pathDigest)
      }
    }

    skipScrollToAndFocusRef.current = true
  }, [isSelectedFile, onScrollToAndFocusEntry, progressiveDiffEntry.pathDigest, progressiveDiffEntry?.renderMode])

  if (!progressiveDiffEntry) return null

  if (
    progressiveDiffEntry.renderMode === 'HIDE' ||
    progressiveDiffEntry.renderMode === 'LAZY_AUTO_LOAD' ||
    progressiveDiffEntry.renderMode === 'EAGER_AUTO_LOAD'
  ) {
    let loadingSkeleton = <></>
    let approximateLineCount = diffSummary?.linesChanged ?? 5
    if (diffSummary?.changeType === 'REMOVED' || diffSummary?.changeType === 'DELETED') {
      approximateLineCount = 5
    }
    if (progressiveDiffEntry.renderMode === 'HIDE') {
      loadingSkeleton = (
        <HiddenDiffEntryLoadingSkeleton
          progressiveDiffEntry={progressiveDiffEntry}
          approximateLineCount={approximateLineCount}
        />
      )
    } else if (progressiveDiffEntry.renderMode === 'LAZY_AUTO_LOAD') {
      loadingSkeleton = (
        <LazyDiffEntryLoadingSkeleton
          progressiveDiffEntry={progressiveDiffEntry}
          approximateLineCount={approximateLineCount}
        />
      )
    } else if (progressiveDiffEntry.renderMode === 'EAGER_AUTO_LOAD') {
      loadingSkeleton = (
        <EagerDiffEntryLoadingSkeleton
          progressiveDiffEntry={progressiveDiffEntry}
          approximateLineCount={approximateLineCount}
        />
      )
    }

    return (
      <div
        role="region"
        id={`diff-${progressiveDiffEntry.pathDigest}`}
        className={clsx(styles.diffTargetable, styles.diff)}
        data-targeted={isSelectedFile}
        key={`${progressiveDiffEntry.pathDigest}_${progressiveDiffEntry.path}`}
      >
        <div className={styles.diffHeaderWrapper}>
          <DiffFileHeader
            areLinesExpanded={false}
            canExpandOrCollapseLines={false}
            fileLinkHref={`#diff-${progressiveDiffEntry.pathDigest}`}
            canToggleRichDiff={false}
            linesAdded={diffSummary?.linesAdded ?? 0}
            linesChanged={diffSummary?.linesChanged ?? 0}
            linesDeleted={diffSummary?.linesDeleted ?? 0}
            newPath={progressiveDiffEntry.path}
            patchStatus={''}
            path={progressiveDiffEntry.path}
            onToggleExpandAllLines={noop}
            onToggleFileCollapsed={noop}
            onToggleDiffDisplay={noop}
            isInSingleFileMode={isInSingleFileMode}
          />
        </div>
        {loadingSkeleton}
      </div>
    )
  }

  if (!diffEntry) {
    return (
      <PullRequestDiffEntryErrorFallback
        linesAdded={0}
        linesChanged={0}
        linesDeleted={0}
        path={progressiveDiffEntry.path}
        pathDigest={progressiveDiffEntry.pathDigest}
        newTreeEntry={undefined}
        oldTreeEntry={undefined}
        status={'MODIFIED'}
      />
    )
  }

  return (
    <ErrorBoundary
      boundaryName="ProgressivePullRequestDiffEntry"
      fallback={<PullRequestDiffEntryErrorFallback {...diffEntry} />}
    >
      <PullRequestDiff
        changeType={diffEntry.status}
        diffLines={diffEntry.diffLines}
        diffManuallyExpanded={diffManuallyExpanded}
        diffSize={diffEntry.diffSize}
        headBranchName={headBranchName}
        headRepoName={headRepoName}
        headRepoOwner={headRepoOwner}
        helpUrl={diffEntry.helpUrl}
        isBinary={diffEntry.isBinary}
        isSelected={isSelectedFile}
        isSubmodule={diffEntry.isSubmodule}
        isTooBig={diffEntry.isTooBig}
        linesAdded={diffEntry.linesAdded}
        linesChanged={diffEntry.linesChanged}
        linesDeleted={diffEntry.linesDeleted}
        newCommitOid={diffEntry.newCommitOid}
        newTreeEntry={diffEntry.newTreeEntry}
        objectId={diffEntry.objectId}
        oldCommitOid={diffEntry.oldCommitOid}
        oldTreeEntry={diffEntry.oldTreeEntry}
        path={diffEntry.path}
        pathDigest={diffEntry.pathDigest}
        pullRequestGlobalRelayId={pullRequestGlobalRelayId}
        repository={repository}
        reviewed={diffEntry.reviewed ?? false}
        richDiff={diffEntry.richDiff}
        status={diffEntry.status}
        submodule={diffEntry.submodule}
        truncatedReason={diffEntry.truncatedReason}
      />
    </ErrorBoundary>
  )
}

try{ ProgressivePullRequestDiffEntry.displayName ||= 'ProgressivePullRequestDiffEntry' } catch {}
try{ ProgressivePullRequestDiffEntryUnmemoized.displayName ||= 'ProgressivePullRequestDiffEntryUnmemoized' } catch {}